import React, {useEffect, useState} from 'react';
import {
    Button,
    Col,
    Form,
    Input,
    Modal,
    Row,
    Spin,
    message,
    InputNumber,
    Select,
    Alert,
    Switch,
    Checkbox, Tooltip, Table, Tabs
} from "antd";
import api, {getApiErrorData} from "../../api";
import './style.css'
import CropTiming from "../../Components/CropTiming";
import {
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
    LockOutlined
} from "@ant-design/icons";
import SearchCropModal from "../../Components/SearchCropModal";
import IconPickerModal from "../../Components/IconPickerModal";
import {useNavigate} from "react-router-dom";
import {DEFAULT_GUTTER, monthOptions} from "../../constants";
import HelpVideoLoader from "../../Components/HelpVideoLoader";
import {useTranslations} from "../../lang";
import TextArea from "antd/lib/input/TextArea";
import CropImage from "../../Components/CropImage";
import {useAuth} from "../../auth";
import OpenCropWebsiteButton from "../../Components/OpenCropWebsiteButton";
import {noteToParagraph, calculateCropDates} from "../../helpers";
import {getMeasurementAddon, formatCropMeasurement} from "../../utils/measurements";
import CropTasksForm from "../../Components/CropTasksForm";

const {confirm} = Modal;

export default function Crops() {
    const [crops, setCrops] = useState([])
    const [cropTypes, setCropTypes] = useState([])
    const [loading, setLoading] = useState(true)
    const [addCropModalOpen, setAddCropModalOpen] = useState(false)
    const [editCropModalOpen, setEditCropModalOpen] = useState(false)
    const [activeTabKey, setActiveTabKey] = useState("1")
    const [editingCrop, setEditingCrop] = useState(null);
    const [editCropForm] = Form.useForm();
    const [filterCropForm] = Form.useForm();
    const [uploadedFile, setUploadedFile] = React.useState(null);
    const navigate = useNavigate();
    const query = new URLSearchParams(window.location.search).get('search')
    const cropId = new URLSearchParams(window.location.search).get('cropId')
    const [isFiltering, setIsFiltering] = useState(false)
    const [reloadingCropData, setReloadingCropData] = useState(false)
    const {translate} = useTranslations()
    const {measurementSystem} = useAuth()
    const distanceUnit = getMeasurementAddon(measurementSystem, 'distance');
    const {plan} = useAuth()

    const [isSeedling, setIsSeedling] = useState(false);
    const [isDirectSown, setIsDirectSown] = useState(false);
    const [isPrickedOut, setIsPrickedOut] = useState(false);
    const [isMultipleHarvestable, setIsMultipleHarvestable] = useState(false);
    const [calculationExample, setCalculationExample] = useState(null);
    const [currentCropData, setCurrentCropData] = useState(null);
    const [iconPickerOpen, setIconPickerOpen] = useState(false);
    const [cropIcon, setCropIcon] = useState(null);

    function setTogglesFromCrop(crop) {
        setIsSeedling(crop.is_seedling);
        setIsDirectSown(crop.is_direct_sown);
        setIsPrickedOut(crop.is_pricked_out);
        setIsMultipleHarvestable(crop.is_multiple_harvestable);
    }

    function handleUpload(event) {
        event.preventDefault();
        api.uploadToCdn(event.target.files[0]).then(setUploadedFile);
    }

    function calculateCropDaysFromCalendar() {
        const crop = editCropForm.getFieldsValue();
        editCropForm.setFieldValue('germination_days', 7)
        editCropForm.setFieldValue('plant_out_days', (crop.plant_min - crop.sow_min) * 30)
        editCropForm.setFieldValue('maturity_days', (crop.harvest_min - crop.plant_min) * 30)
    }

    function loadCrops() {
        console.log('Loading crops')

        setLoading(true)

        const filters = filterCropForm.getFieldsValue()

        setIsFiltering(Object.keys(filters).length > 0)

        api.crops(filters).then((cropsData) => {
            setCrops(cropsData)

            // Handle cropId parameter to open edit modal
            if (cropId) {
                const cropToEdit = cropsData.find(crop => crop.id.toString() === cropId)
                if (cropToEdit) {
                    handleCropClick(cropToEdit)
                }
            }
        }).finally(() => {
            setLoading(false)
        })

        if (query) {
            setAddCropModalOpen(true)
        }
        return
    }


    function handleCropClick(crop) {
        setUploadedFile(crop.image)
        editCropForm.setFieldsValue(crop)
        setEditingCrop(crop)
        setCurrentCropData(crop)
        setCropIcon(crop.icon || null)
        setEditCropModalOpen(true)
        setActiveTabKey("1")
        setTogglesFromCrop(crop)
    }

    function handleEditModalClose() {
        setEditCropModalOpen(false)
        setEditingCrop(null)
        setCurrentCropData(null)
        editCropForm.resetFields()
        setUploadedFile(null)
        setCropIcon(null)

        // Clear URL parameters when closing modal
        const url = new URL(window.location)
        url.searchParams.delete('cropId')
        window.history.replaceState({}, '', url)

        loadCrops()
    }

    function handleFormValuesChange(changedValues, allValues) {
        // Update currentCropData when date fields change
        const dateFields = ['sow_min', 'sow_max', 'plant_min', 'plant_max', 'harvest_min', 'harvest_max'];
        const hasDateFieldChange = Object.keys(changedValues).some(key => dateFields.includes(key));

        if (hasDateFieldChange && currentCropData) {
            setCurrentCropData({
                ...currentCropData,
                ...allValues
            });
        }
    }

    function toggleStock(crop, inStock) {
        const cropData = {...crop}
        cropData.in_stock = inStock
        api.updateCrop(crop.id, cropData)
        setCrops(prev => prev.map(c => c.id === crop.id ? {...c, in_stock: inStock} : c))
    }

    function handleCropSave() {
        const values = editCropForm.getFieldsValue()
        if (editingCrop === null) {
            values.image = uploadedFile
            api.createCrop(values).then(() => {
                handleEditModalClose()
                loadCrops()
            }).catch(error => {
                if (getApiErrorData(error).error === "CropLimitExceededException") {
                    Modal.error({
                        title: translate("Je hebt het maximum aantal gewassen bereikt"),
                        okText: translate("Ga naar account instellingen"),
                        onOk: () => navigate('/account'),
                        content: (
                            <div>
                                <p>{translate("Je hebt het maximum aantal gewassen bereikt voor je account.")}</p>
                                <p>{translate("Wil je meer gewassen toevoegen? Upgrade dan.")}</p>
                            </div>
                        )
                    })
                    return
                }
            })
        } else {
            values.image = uploadedFile
            api.updateCrop(editingCrop.id, values).then(() => {
                handleEditModalClose()
                message.success(translate('De gegevens van het gewas zijn aangepast!'))
            })
        }
    }

    function deleteCrop(crop) {
        // Error code to Dutch message mapping
        const errorMessages = {
            'CROP_NOT_FOUND': translate('Het gewas kon niet gevonden worden'),
            'CROP_HAS_TASKS': translate('Dit gewas heeft nog taken. Verwijder eerst alle taken van dit gewas'),
            'CROP_HAS_DEPENDENCIES': translate('Dit gewas kan niet verwijderd worden omdat het nog in gebruik is')
        };

        confirm({
            title: translate('Weet je zeker dat je dit gewas wilt verwijderen?'),
            content: translate('Het gewas zal ook uit je planning verwijderd worden. Dit kan niet ongedaan worden gemaakt!'),
            onOk() {
                api.deleteCrop(crop.id).then((res) => {
                    if (res && res.success) {
                        message.open({
                            type: 'success',
                            content: translate('Het gewas is verwijderd'),
                        });
                        loadCrops()
                    } else if (res && res.error) {
                        message.open({
                            type: 'error',
                            content: errorMessages[res.error] || translate('Er is een fout opgetreden bij het verwijderen van het gewas'),
                        });
                    }
                }).catch((error) => {
                    // Handle HTTP error responses (400, 500, etc.)
                    const errorCode = error.response?.data?.error;
                    message.open({
                        type: 'error',
                        content: errorMessages[errorCode] || translate('Er is een fout opgetreden bij het verwijderen van het gewas'),
                    });
                })
            },
            onCancel() {
            },
        });


    }

    useEffect(() => {
        loadCrops(true)
        api.cropTypes().then(setCropTypes)
    }, [])

    return (
        <>
            <IconPickerModal
                open={iconPickerOpen}
                onClose={() => setIconPickerOpen(false)}
                onSelect={(icon) => {
                    setCropIcon(icon);
                    setIconPickerOpen(false);
                    if (editingCrop) {
                        api.setCropIcon(editingCrop.id, icon.id);
                    } else {
                        editCropForm.setFieldValue('icon_id', icon.id);
                    }
                }}
            />
            <SearchCropModal
                addCropModalOpen={addCropModalOpen}
                onCancel={() => setAddCropModalOpen(false)}
                onCropFound={(crop) => {
                    setAddCropModalOpen(false)
                    console.log(crop)
                    api.createCrop({product_id: crop.id}).then(() => {
                        loadCrops()
                        message.success(translate('Het gewas is toegevoegd!'))
                    }).catch(error => {
                        if (error.response?.data?.error === "CropLimitExceededException") {
                            Modal.error({
                                title: translate("Je hebt het maximum aantal gewassen bereikt"),
                                okText: translate("Ga naar account instellingen"),
                                onOk: () => navigate('/account'),
                                content: (
                                    <div>
                                        <p>{translate("Je hebt het maximum aantal gewassen bereikt voor je account.")}</p>
                                        <p>{translate("Wil je meer gewassen toevoegen? Upgrade dan.")}</p>
                                    </div>
                                )
                            })
                        }
                    })
                }}
                onAddManually={() => {
                    setEditCropModalOpen(true)
                    setActiveTabKey("1")
                    setCurrentCropData({})
                    setAddCropModalOpen(false)
                }}
            />
            <Modal
                open={editCropModalOpen}
                onCancel={() => handleEditModalClose()}
                onOk={() => handleCropSave()}
                width={"90%"}
            >
                <h1 style={{margin: 0}}>{editCropForm.getFieldValue('name')} {editCropForm.getFieldValue('variety') ? ` - ${editCropForm.getFieldValue('variety')}` : null}</h1>

                <Form form={editCropForm} layout="vertical" disabled={reloadingCropData}
                      onValuesChange={handleFormValuesChange}>
                    <Form.Item name="product_id" hidden><Input/></Form.Item>
                    <Tabs activeKey={activeTabKey} onChange={setActiveTabKey} items={[
                        {
                            key: '1',
                            forceRender: true,
                            label: translate('Algemene informatie'),
                            children: (
                                <>
                                    <Row gutter={12}>
                                        <Col span={12}>
                                            <Form.Item name="name" label={translate("Gewas")}>
                                                <Input placeholder={translate("Bijv. Rode Biet")}/>
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item name="variety" label={translate("Ras")}>
                                                <Input placeholder={translate("Bijv. Kogel 2")}/>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={12}>
                                        <Col span={24}>
                                            <Form.Item name="crop_type_id" label={translate("Gewastype")}>
                                                <Select
                                                    placeholder={translate("Selecteer gewastype")}
                                                    allowClear
                                                    options={cropTypes.map(ct => ({label: ct.name, value: ct.id}))}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={12}>
                                        <Col span={8}>
                                            <Form.Item name="row_distance" label={translate("Rij afstand")}>
                                                <InputNumber style={{width: '100%'}} addonAfter={distanceUnit}/>
                                            </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item name="plant_distance" label={translate("Plant afstand")}>
                                                <InputNumber style={{width: '100%'}} addonAfter={distanceUnit}/>
                                            </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item name="height" label={translate("Hoogte")}>
                                                <InputNumber style={{width: '100%'}} addonAfter={distanceUnit}/>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={12}>
                                        <Col span={8}>
                                            <Form.Item name="url" label={translate("URL / link")}>
                                                <Input/>
                                            </Form.Item>
                                            {editingCrop && (
                                                <Tooltip
                                                    title={translate("Klik op deze knop om de gegevens van het gewas opnieuw op te halen uit de database")}>
                                                    <Button size={"small"} onClick={() => {
                                                        setReloadingCropData(true)
                                                        api.getCropInfo(editingCrop.id).then((crop) => {
                                                            if (crop) {
                                                                editCropForm.setFieldsValue(crop)
                                                                setUploadedFile(crop.image)
                                                                setIsDirectSown(crop.is_direct_sown)
                                                                setIsSeedling(crop.is_seedling)
                                                                setIsPrickedOut(crop.is_pricked_out)
                                                                setIsMultipleHarvestable(crop.is_multiple_harvestable)
                                                                message.success(translate('Gegevens zijn opgehaald!'))
                                                            } else {
                                                                message.error(translate('Sorry, we kunnen dit product niet vinden in onze database.'))
                                                            }
                                                        }).catch(() => {
                                                            message.error(translate('Er is iets misgegaan bij het ophalen van de gegevens'))
                                                        }).finally(() => {
                                                            setReloadingCropData(false)
                                                        })
                                                    }}>{translate('Gegevens opnieuw ophalen')}</Button>
                                                </Tooltip>
                                            )}
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item label={translate("Afbeelding")}>
                                                <input type="file" onChange={handleUpload}/>
                                            </Form.Item>
                                            {uploadedFile &&
                                                <img src={uploadedFile} style={{maxWidth: "100%", maxHeight: '100px'}}
                                                     alt={uploadedFile}/>}
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item label={translate("Icoon")}>
                                                <div
                                                    onClick={() => setIconPickerOpen(true)}
                                                    style={{
                                                        width: 64, height: 64, border: '1px dashed #d9d9d9', borderRadius: 6,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        cursor: 'pointer', background: '#fafafa'
                                                    }}
                                                    title={translate('Icoon kiezen')}
                                                >
                                                    {cropIcon ? (
                                                        <img src={cropIcon.url} alt={cropIcon.name} style={{width: 48, height: 48, objectFit: 'contain'}}/>
                                                    ) : (
                                                        <span style={{fontSize: 11, color: '#999', textAlign: 'center'}}>{translate('Kies icoon')}</span>
                                                    )}
                                                </div>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col span={24}>
                                            <Form.Item label={translate('Notitie')} name={"note"}>
                                                <TextArea placeholder={translate('Voeg een notitie toe')} rows={4}/>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </>
                            )
                        },
                        {
                            key: '2',
                            forceRender: true,
                            label: translate('Zaaidata'),
                            children: (
                                <>
                                    <h2>{translate('Manier van kweken')}</h2>
                                    <Row gutter={12}>
                                        <Col span={12}>
                                            {(!isSeedling) && (
                                                <Form.Item name={"is_direct_sown"} className={"mar-b-0"}
                                                           valuePropName={"checked"}>
                                                    <Checkbox onChange={(e) => setIsDirectSown(e.target.checked)}>
                                                        {translate("Direct zaaien")}
                                                    </Checkbox>
                                                </Form.Item>
                                            )}
                                            {!isDirectSown && (
                                                <Form.Item name={"is_seedling"} valuePropName={"checked"}
                                                           className={"mar-b-0"}>
                                                    <Checkbox onChange={(e) => setIsSeedling(e.target.checked)}>
                                                        {translate("Pootgoed / plantgoed")}
                                                    </Checkbox>
                                                </Form.Item>
                                            )}
                                        </Col>
                                    </Row>

                                    {(!isSeedling) && (
                                        <>
                                            <h2 className={"mar-t-1"}>{translate('Zaaien')}</h2>
                                            <Row gutter={12}>
                                                <Col span={plan?.has_advanced_crops ? 8 : 12}>
                                                    <Form.Item name="sow_min" label={translate("Zaaien vanaf")}>
                                                        <Select options={monthOptions}/>
                                                    </Form.Item>
                                                </Col>
                                                <Col span={plan?.has_advanced_crops ? 8 : 12}>
                                                    <Form.Item name="sow_max" label={translate("Zaaien t/m")}>
                                                        <Select options={monthOptions}/>
                                                    </Form.Item>
                                                </Col>
                                                {(plan?.has_advanced_crops) && (
                                                    <Col span={plan?.has_advanced_crops ? 8 : 12}>
                                                        <Form.Item name={"germination_days"}
                                                                   label={translate('Dagen van zaaien tot kiemen')}>
                                                            <InputNumber style={{width: '100%'}}
                                                                         placeholder={translate('Bijv. 7')}/>
                                                        </Form.Item>
                                                    </Col>
                                                )}
                                            </Row>
                                        </>
                                    )}
                                    {(!isDirectSown) && (
                                        <>
                                            <h2>{translate('Uitplanten / poten')}</h2>
                                            {plan?.has_advanced_crops && (
                                                <Form.Item name={"is_pricked_out"} valuePropName={"checked"}>
                                                    <Checkbox onChange={(e) => setIsPrickedOut(e.target.checked)}>
                                                        {translate("Dit gewas moet verspeend/verpot worden")}
                                                    </Checkbox>
                                                </Form.Item>
                                            )}


                                            <Row gutter={12}>
                                                <Col span={plan?.has_advanced_crops ? 8 : 12}>
                                                    <Form.Item name="plant_min" label={translate("Uitplanten vanaf")}>
                                                        <Select options={monthOptions}/>
                                                    </Form.Item>
                                                </Col>
                                                <Col span={plan?.has_advanced_crops ? 8 : 12}>
                                                    <Form.Item name="plant_max" label={translate("Uitplanten t/m")}>
                                                        <Select options={monthOptions}/>
                                                    </Form.Item>
                                                </Col>

                                                <Col span={plan?.has_advanced_crops ? 8 : 0}>
                                                    <Form.Item name={"plant_out_days"}
                                                               label={translate(isPrickedOut ? 'Dagen van verspenen tot uitplanten' : 'Dagen van kiemen tot uitplanten')}>
                                                        <InputNumber style={{width: '100%'}}
                                                                     placeholder={translate('Bijv. 60')}/>
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <Row gutter={12}>
                                                {isPrickedOut && (
                                                    <Col span={12}>
                                                        <Form.Item name={"prick_out_days"}
                                                                   label={translate('Dagen van kiemen tot verspenen/verpotten')}>
                                                            <InputNumber style={{width: '100%'}}
                                                                         placeholder={translate('Bijv. 14')}/>
                                                        </Form.Item>
                                                    </Col>
                                                )}
                                            </Row>
                                        </>
                                    )}
                                    <h2>{translate('Oogsten')}</h2>
                                    <Form.Item name={"is_multiple_harvestable"} valuePropName={"checked"}>
                                        <Checkbox onChange={(e) => setIsMultipleHarvestable(e.target.checked)}>
                                            {translate("Dit gewas is meerdere keren te oogsten")}
                                        </Checkbox>
                                    </Form.Item>
                                    <Row gutter={12}>
                                        <Col span={plan?.has_advanced_crops ? 8 : 12}>
                                            <Form.Item name="harvest_min" label={translate("Oogsten vanaf")}>
                                                <Select options={monthOptions}/>
                                            </Form.Item>
                                        </Col>
                                        <Col span={plan?.has_advanced_crops ? 8 : 12}>
                                            <Form.Item name="harvest_max" label={translate("Oogsten t/m")}>
                                                <Select options={monthOptions}/>
                                            </Form.Item>
                                        </Col>

                                        <Col span={plan?.has_advanced_crops ? 8 : 0}>
                                            <Form.Item name={"maturity_days"}
                                                       label={translate(isDirectSown ? 'Aantal dagen van kiemen tot oogst' : 'Dagen van uitplanten tot oogst')}>
                                                <InputNumber style={{width: '100%'}}
                                                             placeholder={translate('Bijv. 60')}/>
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    {isMultipleHarvestable && (
                                        <Row gutter={DEFAULT_GUTTER}>
                                            <Col span={12}>
                                                <Form.Item name={"harvest_count"}
                                                           label={translate('Aantal keer te oogsten')}>
                                                    <InputNumber style={{width: '100%'}}
                                                                 placeholder={translate('Bijv. 3')}/>
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item name={"harvest_days"}
                                                           label={translate('Aantal dagen tussen oogsten')}>
                                                    <InputNumber style={{width: '100%'}}
                                                                 placeholder={translate('Bijv. 7')}/>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    )}

                                    {plan?.has_advanced_crops && (
                                        <Row>

                                            <Col span={24}>
                                                <Button onClick={() => calculateCropDaysFromCalendar()}
                                                        className={"mar-r-1"}>
                                                    {translate('Bereken dagen op basis van zaaikalender')}
                                                </Button>
                                                <Button onClick={() => {
                                                    const cropData = {
                                                        ...editCropForm.getFieldsValue(),
                                                        is_fully_configured: true
                                                    }
                                                    const d = calculateCropDates(cropData)
                                                    setCalculationExample(d)
                                                }}>{translate('Toon rekenvoorbeeld')}</Button>
                                            </Col>
                                        </Row>
                                    )}

                                    {currentCropData && (
                                        <Row className={"mar-t-1"}>
                                            <Col span={24}>
                                                <h3>{translate('Zaaikalender')}</h3>
                                                <CropTiming crop={currentCropData}/>
                                            </Col>
                                        </Row>
                                    )}


                                    <Modal footer={null} open={calculationExample !== null}
                                           onCancel={() => setCalculationExample(null)}>
                                        <h1>{translate('Rekenvoorbeeld')}</h1>
                                        <table style={{width: '100%'}}>
                                            <tbody>
                                            <tr>
                                                <th>{translate('Zaaien')}</th>
                                                <td>{calculationExample?.sow_at.format('DD MMM')}</td>
                                            </tr>
                                            <tr>
                                                <th>{translate('Kiemen')}</th>
                                                <td>{calculationExample?.germinate_at.format('DD MMM')}</td>
                                                <td>+{editCropForm.getFieldValue('germination_days')} {translate('dagen')}</td>
                                            </tr>
                                            {!isDirectSown && (
                                                <>
                                                    {isPrickedOut && (
                                                        <tr>
                                                            <th>{translate('Verspenen')}</th>
                                                            <td>{calculationExample?.prick_at.format('DD MMM')}</td>
                                                            <td>+{editCropForm.getFieldValue('prick_out_days')} {translate('dagen')}</td>
                                                        </tr>
                                                    )}
                                                    <tr>
                                                        <th>{translate('Uitplanten')}</th>
                                                        <td>{calculationExample?.transplant_at.format('DD MMM')}</td>
                                                        <td>+{editCropForm.getFieldValue('plant_out_days')} {translate('dagen')}</td>
                                                    </tr>
                                                </>
                                            )}
                                            {!isMultipleHarvestable && (
                                                <tr>
                                                    <th>{translate('Oogsten')}</th>
                                                    <td>{calculationExample?.harvest_at.format('DD MMM')}</td>
                                                    <td>+{editCropForm.getFieldValue('maturity_days')} {translate('dagen')}</td>
                                                </tr>
                                            )}
                                            {calculationExample?.harvest_dates?.map((date, index) => (
                                                <tr key={index}>
                                                    <th>{translate('Oogst')} {index + 1}</th>
                                                    <td>{date.format('DD MMM')}</td>
                                                    <td>+{isMultipleHarvestable && index === 0 ? editCropForm.getFieldValue('maturity_days') : editCropForm.getFieldValue('harvest_days')} {translate('dagen')}</td>
                                                </tr>
                                            ))}
                                            {isMultipleHarvestable && (
                                                <tr>
                                                    <th>{translate('Laatste oogst')}</th>
                                                    <td>{calculationExample?.harvest_until.format('DD MMM')}</td>
                                                </tr>
                                            )}
                                            </tbody>
                                        </table>
                                    </Modal>
                                </>
                            )
                        },
                        {
                            key: '3',
                            forceRender: true,
                            label: !plan?.has_selling ? (
                                <Tooltip title={translate('Je moet je pakket upgraden om te kunnen verkopen')}>
                                    <span>
                                        <LockOutlined style={{marginRight: '8px'}}/>
                                        {translate('Verkoop')}
                                    </span>
                                </Tooltip>
                            ) : translate('Verkoop'),
                            disabled: !plan?.has_selling,
                            children: (
                                <div style={{padding: '20px 0', textAlign: 'center', color: '#999'}}>
                                    {translate('Binnenkort beschikbaar')}
                                </div>
                            )
                        },
                        {
                            key: '4',
                            forceRender: true,
                            label: translate('Taken'),
                            children: (
                                <CropTasksForm
                                    cropId={editingCrop?.id}
                                    isDirectSown={isDirectSown}
                                    isSeedling={isSeedling}
                                    hasTeam={plan?.has_team}
                                />
                            )
                        },
                        {
                            key: '5',
                            forceRender: true,
                            label: translate('Instructies'),
                            children: (
                                <>
                                    <Form.Item name="sowing_instruction" label={translate("Zaaiinstructies")}>
                                        <TextArea rows={4}/>
                                    </Form.Item>
                                    <Form.Item name="transplant_instructions" label={translate("Verplantinstructies")}>
                                        <TextArea rows={4}/>
                                    </Form.Item>
                                    <Form.Item name="harvest_instruction" label={translate("Oogstinstructies")}>
                                        <TextArea rows={4}/>
                                    </Form.Item>
                                </>
                            )
                        }
                    ]}/>
                </Form>
            </Modal>
            <Row gutter={[12, 12]} className={"mar-b-1"}>
                <Col md={12} xs={12}>
                    <h1>{translate("Gewassen")}</h1>
                </Col>
                <Col md={12} xs={12} style={{textAlign: 'right'}}>
                    <Button
                        danger
                        className={"mar-r-1"}
                        onClick={() => {
                            Modal.confirm({
                                title: translate('Weet je het zeker?'),
                                content: translate('Weet je zeker dat je al je gewassen wilt markeren dat je ze niet op voorraad hebt? Dit kan niet ongedaan worden.'),
                                okText: translate('Ja'),
                                cancelText: translate('Annuleren'),
                                okButtonProps: {danger: true},
                                onOk: () => {
                                    return api.clearCropStock().then(() => {
                                        message.success(translate('Alle gewassen zijn op niet op voorraad gezet'));
                                        loadCrops();
                                    });
                                }
                            });
                        }}
                    >
                        {translate('Alles niet op voorraad')}
                    </Button>
                    <Button type="primary" size={"large"} onClick={() => setAddCropModalOpen(true)}>
                        {translate('Gewas toevoegen')}
                    </Button>
                </Col>
            </Row>


            <Form form={filterCropForm} layout={"vertical"}>
                <Row gutter={[12, 12]}>
                    <Col md={4} xs={12}>
                        <Form.Item name={"search"} label={translate("Naam en/of ras")}>
                            <Input onPressEnter={loadCrops} placeholder={translate("Zoeken op naam en/of ras")}/>
                        </Form.Item>
                    </Col>
                    <Col md={3} xs={12}>
                        <Form.Item name={"sow_in"} label={translate("Zaaiperiode")}>
                            <Select mode={"multiple"}
                                    placeholder={translate("Filter op zaaiperiode")}
                                    defaultValue={[]}
                                    options={monthOptions}
                                    allowClear={true}
                            />
                        </Form.Item>
                    </Col>
                    <Col md={3} xs={12}>
                        <Form.Item name={"plant_in"} label={translate("Plantperiode")}>
                            <Select mode={"multiple"}
                                    placeholder={translate("Filter op (ver)plantperiode")}
                                    defaultValue={[]}
                                    options={monthOptions}
                                    allowClear={true}
                            />
                        </Form.Item>
                    </Col>
                    <Col md={3} xs={12}>
                        <Form.Item name={"harvest_in"} label={translate("Oogstperiode")}>
                            <Select mode={"multiple"}
                                    placeholder={translate("Filter op oogstperiode")}
                                    defaultValue={[]}
                                    options={monthOptions}
                                    allowClear={true}
                            />
                        </Form.Item>
                    </Col>
                    <Col md={2} xs={12}>
                        <Form.Item name={"crop_type_id"} label={translate("Gewastype")}>
                            <Select
                                placeholder={translate("Alle")}
                                allowClear={true}
                                options={cropTypes.map(ct => ({label: ct.name, value: ct.id}))}
                            />
                        </Form.Item>
                    </Col>
                    <Col md={3} xs={12}>
                        <Form.Item label={translate("Sorteer")} name={"sort"}>
                            <Select
                                defaultValue={"id_desc"} options={[
                                {
                                    label: translate("Nieuwste eerst"),
                                    value: "id_desc"
                                },
                                {
                                    label: translate("Oudste eerst"),
                                    value: "id_asc"
                                },
                                {
                                    label: translate("Zaaiperiode"),
                                    value: "sow_min_asc"
                                },
                                {
                                    label: translate("Plantperiode"),
                                    value: "plant_min_asc"
                                },
                                {
                                    label: translate("Oogstperiode"),
                                    value: "harvest_min_asc"
                                }
                            ]}></Select>
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={2}>
                        <Form.Item name={"stock_status"} label={translate('Op voorraad')}>
                            <Select
                                defaultValue={"all_stock"} options={[
                                {
                                    label: translate("Alle"),
                                    value: "all_stock"
                                },
                                {
                                    label: translate("Op voorraad"),
                                    value: "in_stock"
                                },
                                {
                                    label: translate("Niet op voorraad"),
                                    value: "no_stock"
                                }
                            ]}></Select>
                        </Form.Item>
                    </Col>
                    <Col md={2} xs={12}>
                        <Form.Item label={<></>}>
                            <Button type={"primary"} onClick={() => {
                                loadCrops()
                            }}>{translate("Zoeken")}</Button>
                        </Form.Item>

                    </Col>
                    <Col md={2} xs={12}>
                        <Form.Item label={<></>}>
                            <Button onClick={() => {
                                filterCropForm.resetFields()
                                // loadCrops()
                            }}>{translate('Reset')}</Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            {loading && (
                <Spin/>
            )}

            {(!loading && !isFiltering && crops.length === 0) && (
                <>
                    <Alert
                        description={translate("Je hebt nog geen gewassen in je account staan. Klop op de knop `Gewas toevoegen` om ze direct toe tevoegen! :)")}></Alert>
                    <HelpVideoLoader/>
                </>
            )}
            {(!loading && isFiltering && crops.length === 0) && (
                <Alert
                    description={translate("Helaas, er zijn geen gewassen gevonden die voldoen aan de filters. Probeer het opnieuw met andere filters.")}></Alert>
            )}

            <Table
                dataSource={crops}
                onRow={(crop) => ({
                    onClick: () => handleCropClick(crop),
                    style: { cursor: 'pointer' }
                })}
                columns={[
                {
                    responsive: ['xs'],
                    title: "Gewassen",
                    width: "100%",
                    key: 'id',
                    render: (text, crop) => {
                        return (
                            <>
                                <h2>{crop.name} {crop.variety ? ` - ${crop.variety}` : null}</h2>

                                <Row gutter={[12, 12]}>
                                    <Col span={8}>
                                        <CropImage crop={crop} border={true} width={"100%"}/>
                                    </Col>
                                    <Col span={16}>
                                        <CropTiming crop={crop}/>
                                    </Col>
                                </Row>

                                {crop.note && (
                                    noteToParagraph(crop.note)
                                )}
                                <Row gutter={[12, 12]} className={"mar-t-1"}>
                                    <Col>
                                        <Button size={"small"}
                                                type={"primary"}
                                                style={{background: crop.in_stock ? '#52c41a' : '#ff4d4f', borderColor: crop.in_stock ? '#52c41a' : '#ff4d4f'}}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleStock(crop, !crop.in_stock);
                                                }}
                                                className={"mar-r-1"}>
                                            {crop.in_stock ? <><CheckOutlined/> {translate('Op voorraad')}</> : <><CloseOutlined/> {translate('Geen voorraad')}</>}
                                        </Button>
                                        <Button size={"small"} danger onClick={(e) => {
                                            e.stopPropagation();
                                            deleteCrop(crop);
                                        }}
                                                className={"mar-r-1"}>
                                            <DeleteOutlined/>
                                        </Button>
                                        <OpenCropWebsiteButton label={false} crop={crop} size={"small"}/>
                                    </Col>
                                </Row>
                            </>
                        )
                    }
                },
                {
                    responsive: ['md'],
                    title: "",
                    width: "50px",
                    render: (text, crop) => {
                        return (
                            <CropImage crop={crop} border={true} width={"50px"}/>
                        )
                    },
                    key: "image",
                },
                {
                    responsive: ['md'],
                    title: translate("Gewas"),
                    render(text, crop) {
                        return (
                            <div>
                                <h4>{crop.name} {crop.variety ? ` - ${crop.variety}` : null}</h4>
                                {crop.row_distance && crop.plant_distance && (
                                    <p>{translate('Plantafstand:')} {formatCropMeasurement(crop, 'row_distance', measurementSystem)}x{formatCropMeasurement(crop, 'plant_distance', measurementSystem)}</p>
                                )}
                                {crop.sowing_depth && (
                                    <p>{translate('Zaaidiepte:')} {formatCropMeasurement(crop, 'sowing_depth', measurementSystem)}</p>
                                )}
                            </div>
                        )
                    },
                    key: "name",
                },
                {
                    responsive: ['md'],
                    title: translate("Timing"),
                    render(text, crop) {
                        return (
                            <CropTiming crop={crop}/>
                        )
                    },
                    key: "timing",
                },
                {
                    responsive: ['md'],
                    title: translate("Acties"),
                    render(text, crop) {
                        return (
                            <div style={{textAlign: 'right'}}>
                                <Tooltip title={crop.in_stock ? translate('Op voorraad') : translate('Geen voorraad')}>
                                    <Button size={"small"}
                                            type={"primary"}
                                            style={{background: crop.in_stock ? '#52c41a' : '#ff4d4f', borderColor: crop.in_stock ? '#52c41a' : '#ff4d4f'}}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleStock(crop, !crop.in_stock);
                                            }}
                                            className={"mar-r-1"}>
                                        {crop.in_stock ? <CheckOutlined/> : <CloseOutlined/>}
                                    </Button>
                                </Tooltip>
                                <Button size={"small"} danger onClick={(e) => {
                                    e.stopPropagation();
                                    deleteCrop(crop);
                                }}
                                        className={"mar-r-1"}>
                                    <DeleteOutlined/>
                                </Button>
                                <OpenCropWebsiteButton label={false} crop={crop} size={"small"}/>
                            </div>
                        )
                    },
                }
            ]}/>

            {/*{crops.splice(0, 10).map(crop => {*/}
            {/*    return (*/}
            {/*        <Row key={crop.id} gutter={[18, 18]} className={"mar-b-1"} style={{background:'white', padding: '8px'}}>*/}
            {/*            <Col md={2} xs={24}>*/}
            {/*                <CropImage crop={crop} border={true} width={"100%"}/>*/}
            {/*            </Col>*/}
            {/*            <Col md={22} xs={24}>*/}
            {/*                <Row>*/}
            {/*                    <Col md={10}>*/}
            {/*                        <h2>{crop.name} {crop.variety ? ` - ${crop.variety}` : null}</h2>*/}
            {/*                        {crop.row_distance && crop.plant_distance && (*/}
            {/*                            <p>{translate('Plantafstand:')} {crop.row_distance}x{crop.plant_distance}cm</p>*/}
            {/*                        )}*/}
            {/*                    </Col>*/}
            {/*                    <Col md={10} xs={24}><CropTiming crop={crop}/></Col>*/}
            {/*                    <Col md={4} xs={12}>*/}

            {/*                        <Button size={"small"} onClick={() => handleCropClick(crop)} type="primary"*/}
            {/*                                className={"mar-b-1 mar-l-1"}>*/}
            {/*                            <EditOutlined/>*/}
            {/*                        </Button>*/}
            {/*                        <Button size={"small"} danger onClick={() => deleteCrop(crop)}*/}
            {/*                                className={"mar-l-1"}>*/}
            {/*                            <DeleteOutlined/>*/}
            {/*                        </Button>*/}

            {/*                        {plan.has_team && (*/}
            {/*                            <>*/}
            {/*                                <Button size={"small"} onClick={() => navigate(`/crops/${crop.id}`)}*/}
            {/*                                        type="primary"*/}
            {/*                                        className={"mar-r-1 mar-l-1"}>*/}
            {/*                                    <SettingOutlined/>*/}
            {/*                                </Button>*/}
            {/*                            </>*/}
            {/*                        )}*/}
            {/*                        <OpenCropWebsiteButton label={false} crop={crop} size={"small"} />*/}
            {/*                    </Col>*/}
            {/*                    <Col md={12} xs={12}>*/}
            {/*                        <Switch size={"small"} defaultChecked={crop.in_stock}*/}
            {/*                                onChange={(checked) => toggleStock(crop, checked)}/> {translate('Op voorraad')}*/}
            {/*                    </Col>*/}
            {/*                </Row>*/}

            {/*            </Col>*/}
            {/*        </Row>*/}
            {/*    )*/}
            {/*})}*/}
        </>
    )
}